import React, { useEffect, useState } from "react";
import {
	Button,
	TextField,
	CircularProgress,
	Icon,
} from "@material-ui/core";

import { useFormik } from "formik";
import * as Yup from "yup";

import OftadehLayout from "../../components/OftadehLayout/OftadehLayout";

import { useStore } from "../../stores/helpers/useStore";
import { useIsMounted } from "../../helpers/useIsMounted";
import { observer } from "mobx-react-lite";

import labels from "../../utils/labels";
import "./BrandPage.css";

const validationSchema = Yup.object().shape({
	customTitle: Yup.string().trim(),
	customUrl: Yup.string()
		.trim()
		.when("customUrlTitle", {
			is: (val) => !!val?.length,
			then: Yup.string().required("Please, add Custom URL!"),
		})
		.url("Invalid url!"),
	customUrlTitle: Yup.string().trim(),
});

const BrandPage = () => {
	const [savingError, setSavingError] = useState("");
	const isMounted = useIsMounted();
	const {
		settingsStore: {
			customBranding: { customTitle, customUrl, customUrlTitle },
			getCustomBrandingSettings,
			editCustomBrandingSettings,
		},
		uiStore: { loading },
	} = useStore();

	const formik = useFormik({
		initialValues: {
			customTitle,
			customUrl,
			customUrlTitle,
		},
		validationSchema,
		enableReinitialize: true,
		onSubmit: async (values) => {
			if (JSON.stringify(values) === JSON.stringify(formik.initialValues))
				return;

			try {
				await editCustomBrandingSettings(values);
			} catch {
				setSavingError("Saving failed, please try again!");
			}
		},
	});

	useEffect(() => {
		setSavingError("");
	}, [formik.values]);

	useEffect(() => {
		getCustomBrandingSettings();
	}, [getCustomBrandingSettings]);
	return (
		<OftadehLayout loading={loading || !isMounted}>
			<div className="brand-page-header">
				<h1>{labels.brand_title}</h1>
				<p className="brand-page-subtitle">
					Customize your trivia experience with your own branding
				</p>
			</div>

			<div className="brand-info-banner">
				<div className="brand-info-banner-content">
					<Icon className="brand-info-icon">info</Icon>
					<div className="brand-info-text">
						<p>{labels.brand_description}</p>
					</div>
				</div>
			</div>

			<div className="brand-form-container">
				<form onSubmit={formik.handleSubmit}>
					<div className="brand-form-section">
						<div className="brand-form-group">
							<label className="brand-form-label">
								{window.labs.input_brand_title}
							</label>
							<p className="brand-form-description">
								This title will be displayed in your trivia application instead of the default branding.
							</p>
							<TextField
								name="customTitle"
								type="text"
								fullWidth
								variant="outlined"
								placeholder="Enter your custom title"
								disabled={formik.isSubmitting}
								value={formik.values.customTitle || ""}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={
									!!formik.touched.customTitle && !!formik.errors.customTitle
								}
								helperText={
									formik.touched.customTitle && formik.errors.customTitle
								}
							/>
						</div>

						<div className="brand-form-group">
							<label className="brand-form-label">
								Custom URL
							</label>
							<p className="brand-form-description">
								Add a custom link that will be displayed in your trivia application. Must be a valid URL (e.g., https://yourwebsite.com).
							</p>
							<TextField
								name="customUrl"
								type="text"
								fullWidth
								variant="outlined"
								placeholder="https://yourwebsite.com"
								disabled={formik.isSubmitting}
								value={formik.values.customUrl || ""}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={
									!!formik.touched.customUrl && !!formik.errors.customUrl
								}
								helperText={
									formik.touched.customUrl && formik.errors.customUrl
								}
							/>
						</div>

						<div className="brand-form-group">
							<label className="brand-form-label">
								Custom URL Label
							</label>
							<p className="brand-form-description">
								The text that will be displayed for your custom URL link (e.g., "Visit Our Website").
							</p>
							<TextField
								name="customUrlTitle"
								type="text"
								fullWidth
								variant="outlined"
								placeholder="Visit Our Website"
								disabled={formik.isSubmitting}
								value={formik.values.customUrlTitle || ""}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={
									!!formik.touched.customUrlTitle &&
									!!formik.errors.customUrlTitle
								}
								helperText={
									formik.touched.customUrlTitle &&
									formik.errors.customUrlTitle
								}
							/>
						</div>

						{savingError && (
							<div className="brand-error-message">
								{savingError}
							</div>
						)}

						<div className="brand-form-actions">
							<Button
								variant="contained"
								color="primary"
								onClick={formik.handleSubmit}
								disabled={
									formik.isSubmitting ||
									JSON.stringify(formik.values) ===
										JSON.stringify(formik.initialValues)
								}
								className="brand-save-button"
								startIcon={formik.isSubmitting ? null : <Icon>save</Icon>}
							>
								{formik.isSubmitting ? (
									<span className="brand-loading-spinner">
										Saving
										<CircularProgress color="inherit" size={16} />
									</span>
								) : (
									"Save Changes"
								)}
							</Button>
						</div>
					</div>
				</form>
			</div>
		</OftadehLayout>
	);
};

export default observer(BrandPage);
